/**
 * One draft: what to check on the left, the letter as it will read on the right, and the
 * download — enabled only when the proxy says the draft is complete and every point is ticked.
 */
import { type ReactNode, useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@finn-auto/design-system/atoms/select';
import { Banner } from '@finn-auto/design-system/atoms/banner';
import { Download, Loader2 } from 'lucide-react';
import { BlockChoices, FlagList, TaskLines, ValueFields } from './DraftControls';
import { useReferenceLetterDraft } from './useReferenceLetterDraft';
import { useLetterSignature } from './useLetterSignature';
import { HeaderStatus, STATUS_LINE_ID, SignatureControl, SignatureError, StatusLine } from './SignatureControl';
import { docxVariant } from './headerButtons';
import { DoneMenu } from './DoneMenu';
import { formatDate } from './format';
import { isInterimTemplate, templateLabel } from './templateLabels';
import type { ReferenceLetterDraft } from '../../types/referenceLetterDraft';

// The verbatim case has its own point to confirm above, so it needs no second note here.
// Every box on the controls side is the same card: one border, one corner, one padding.
const CARD = 'rounded border border-solid border-pearl bg-white p-4';

const MODEL_NOTE = "Boris reworded the supervisor's task notes into these lines. He added nothing, and judging words are refused.";

/**
 * The letter's fields, folded away unless one is missing. Only the reviewer closes it: deriving
 * `open` on every render closed it the moment the last missing field was filled, with the cursor
 * still inside.
 *
 * A native <details>, not the DS Accordion — a documented deviation: the Accordion keeps its content
 * mounted at h-0 and its content wrapper carries the design system's trimming ::before/::after,
 * which pulled form content out of place on the People Tickets form (see CLAUDE.md).
 */
function DetailsSection({ needed, children }: { needed: boolean; children: ReactNode }) {
  const [open, setOpen] = useState(needed);
  // A field that goes missing again (a template switch wipes the reviewer's values) opens it once
  // more; nothing ever closes it on the reviewer's behalf. Adjusted during render, React's pattern
  // for state that follows a prop.
  const [wasNeeded, setWasNeeded] = useState(needed);
  if (needed !== wasNeeded) {
    setWasNeeded(needed);
    if (needed) setOpen(true);
  }
  return (
    <details open={open} onToggle={(e) => setOpen(e.currentTarget.open)} data-testid="details" className={CARD}>
      <summary className="cursor-pointer text-sm font-semibold text-zinc-900">
        Details <span className="font-normal text-zinc-600">· name, dates, signatory</span>
      </summary>
      <div className="mt-3">{children}</div>
    </details>
  );
}

/** A titled card in the left column. */
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className={`flex flex-col gap-3 ${CARD}`}>
      <h3 className="text-sm font-semibold text-zinc-900">{title}</h3>
      {children}
    </section>
  );
}

/** ⟦marker⟧ is what the proxy renders where Boris still needs an answer. */
function Paragraph({ text }: { text: string }) {
  const parts = text.replace(/\t+/g, ' ').split(/(⟦[^⟧]*⟧)/);
  return (
    <>
      {parts.map((p, i) =>
        p.startsWith('⟦') ? (
          <mark key={i} className="rounded bg-lightOrange px-1 text-orange">
            {p.slice(1, -1)}
          </mark>
        ) : (
          <span key={i}>{p}</span>
        ),
      )}
    </>
  );
}

/**
 * The letter as it will read, with ⟦markers⟧ where Boris still needs an answer — set as a sheet of
 * paper on a grey desk, so it reads as the document and not as one more box of controls.
 */
function Preview({ draft }: { draft: ReferenceLetterDraft }) {
  return (
    <div className="rounded bg-cotton p-3 sm:p-6">
      <article data-testid="letter-page" className="mx-auto max-w-[42rem] bg-white px-6 py-8 text-sm leading-relaxed text-zinc-900 shadow-md sm:px-10 sm:py-12">
      {draft.paragraphs.map((p, i) =>
        !p.text.trim() ? (
          <div key={i} className="h-3" />
        ) : p.numbered ? (
          <p key={i} className="ml-4 list-item list-disc whitespace-pre-wrap">
            <Paragraph text={p.text} />
          </p>
        ) : (
          <p key={i} className="whitespace-pre-wrap">
            <Paragraph text={p.text} />
          </p>
        ),
      )}
      </article>
    </div>
  );
}

/**
 * One request's draft: points to check, choices and fields on the left, the letter on the right.
 * `onSignatureChange` is told after a send or cancel, so the list behind it can reload.
 */
export function DraftWorkspace({ recordId, onSignatureChange }: { recordId: string; onSignatureChange?: () => void }) {
  const d = useReferenceLetterDraft(recordId);
  const sig = useLetterSignature(recordId, { onChange: onSignatureChange });
  const { draft } = d;

  if (!draft) {
    return d.error ? (
      <Banner variant="alert" description={d.error} />
    ) : (
      <p className="flex items-center gap-2 text-sm text-zinc-600">
        <Loader2 className="h-4 w-4 animate-spin" /> Boris is drafting the letter…
      </p>
    );
  }

  const open = draft.flags.filter((f) => f.level === 'blocking' || !f.confirmed).length;
  const e = draft.employee;
  const employeeLine = `${e?.position ?? '–'} · ${e?.hireDate ? formatDate(e.hireDate) : '?'} – ${e?.endDate ? formatDate(e.endDate) : isInterimTemplate(draft.template) ? 'today' : 'end date missing'}${draft.followOn ? ` · continues as ${draft.followOn.position ?? 'another role'} from ${formatDate(draft.followOn.hireDate)}` : ''}`;
  const grades = draft.blocks.filter((b) => b.kind === 'grade');
  const wording = draft.blocks.filter((b) => b.kind !== 'grade');
  // Details start folded away unless Boris is missing one of them.
  const detailsNeeded = !draft.gender || Object.values(draft.values).some((v) => !v);
  const progress = { open, blocking: draft.flags.some((f) => f.level === 'blocking'), updating: d.loading || d.pending };
  // One main button per stage: Send before signing, the signed PDF after (`docxVariant`).
  const docx = docxVariant(draft, sig);
  return (
    <div className="flex flex-col gap-6">
      {/* Pinned: the template, the status and Download stay in reach while the letter is read. */}
      {/* One line from lg up (the employee line truncates): the letter column below is pinned at
          a fixed offset under it, and a wrapped header would cover the letter's top. */}
      <header data-testid="draft-header" className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-4 rounded border border-solid border-pearl bg-white px-4 py-3 shadow-sm lg:flex-nowrap">
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-sm font-semibold text-zinc-900">{e?.name ?? 'Unknown employee'}</h2>
          <p className="truncate text-sm text-zinc-600" title={employeeLine}>
            {employeeLine}
          </p>
          <StatusLine draft={draft} sig={sig} progress={progress} />
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-3">
          <HeaderStatus sig={sig} />
          <SignatureControl draft={draft} request={d.request} updating={d.loading || d.pending} sig={sig} />
          <Button type="button" variant={docx} size="md" className="rounded" aria-label="Download .docx" aria-describedby={STATUS_LINE_ID} onClick={d.download} disabled={!draft.downloadable || d.loading || d.downloading}>
            {d.downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} {docx === 'ghost' ? '.docx' : 'Download .docx'}
          </Button>
          {sig.view && <DoneMenu done={Boolean(sig.view.done)} onToggle={sig.setDone} label={e?.name ?? 'this letter'} />}
        </div>
      </header>

      <SignatureError sig={sig} />
      {d.error && <Banner variant="alert" description={d.error} />}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="flex flex-col gap-4">
          {/* Beside the letter rather than in the header, which it crowded (David, Oct 14 2026). */}
          <Section title="Template">
            <Select value={draft.template} onValueChange={d.switchTemplate}>
              <SelectTrigger size="md" aria-label="Template" className="w-full rounded">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {draft.templates.map((t) => (
                  <SelectItem key={t} value={t}>
                    {templateLabel(t)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Section>
          <Section title="To check">
            <FlagList flags={draft.flags} onConfirm={d.confirm} />
          </Section>
          {grades.length > 0 && (
            <Section title="Grades">
              <BlockChoices blocks={grades} onChoose={d.choose} showText />
            </Section>
          )}
          {wording.length > 0 && (
            <Section title="Wording">
              <BlockChoices blocks={wording} onChoose={d.choose} />
            </Section>
          )}
          <DetailsSection needed={detailsNeeded}>
            <ValueFields draft={draft} onValue={d.setValue} onGender={d.setGender} />
          </DetailsSection>
          <Section title="Tasks">
            {d.firstTaskSource === 'model' && <p className="text-xs text-zinc-600">{MODEL_NOTE}</p>}
            <TaskLines key={draft.tasks.join('\u0000')} tasks={draft.tasks} onChange={d.setTasks} />
          </Section>
        </div>
        {/* The letter stays in view while the controls on the left scroll past, below the one-line
            pinned header (top-24); the page's scroll area is 100vh less main's padding, hence -10rem. */}
        <div data-testid="letter-column" className="flex flex-col gap-2 lg:sticky lg:top-24 lg:max-h-[calc(100vh-10rem)] lg:self-start lg:overflow-auto">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-zinc-900">
            Letter {d.loading && <Loader2 className="h-3 w-3 animate-spin" />}
          </h3>
          <Preview draft={draft} />
        </div>
      </div>
    </div>
  );
}
